import { useEffect, useRef } from "react";
import { useFieldArray } from "react-hook-form";
import type { Control } from "react-hook-form";
import styles from "./FieldArray.module.scss";
import Button from "@/components/Button/Button";
import DeleteIcon from "@/components/SVGComponents/DeleteIcon";
import { Input } from "../Input/Input";
import withLabelAndError from "../withLabelAndError";
import { combineClasses, getInitialValues } from "@/lib/utils";

interface SlabFieldConfig {
  controlType?: string;
  label?: string;
  placeholder?: string;
  defaultValue?: unknown;
}

export interface FieldArrayProps {
  control: Control<any>;
  name: string;
  slabFields: Record<string, SlabFieldConfig>;
}

let updated = false;

const FieldArray = ({ control, name, slabFields }: FieldArrayProps) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  const inputRef = useRef<HTMLInputElement>(null);

  const addSlab = () => {
    updated = true;
    append(getInitialValues(slabFields as Record<string, { defaultValue: unknown }>));
  };

  const handleRemove = (index: number) => () => {
    updated = true;
    remove(index);
  };

  useEffect(() => {
    if (inputRef?.current && updated) {
      inputRef.current.focus();
    }
  }, [fields]);

  useEffect(() => {
    return () => {
      updated = false;
    };
  }, []);

  return (
    <div className={styles.fieldArrayWrapper}>
      {fields.map((item, index) => {
        const lastSlab = index === fields.length - 1;
        return (
          <div key={item.id} className={styles.slabRow}>
            <div className={styles.heading}>
              <p>Slab {index + 1}</p>
              {lastSlab && <DeleteIcon onClick={handleRemove(index)} height={16} />}
            </div>
            <div className={styles.fields}>
              {Object.entries(slabFields).map(([fieldName, fieldProps], slabFieldIndex) => {
                return (
                  <Input
                    key={fieldName}
                    {...(lastSlab && slabFieldIndex === 0 ? { ref: inputRef } : {})}
                    name={`${name}[${index}][${fieldName}]`}
                    control={control}
                    type={fieldProps.controlType}
                    placeholder={fieldProps?.placeholder || `Enter ${fieldProps.label}`}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
      <Button type="button" onClick={addSlab} className={combineClasses(styles.addBtn)}>
        Add Slab
      </Button>
    </div>
  );
};

const FieldArrayWithLabelAndError = withLabelAndError<FieldArrayProps>(FieldArray);

export default FieldArrayWithLabelAndError;
export { FieldArray };
